import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import Header from "../../components/Header";
import RailSixSigmaPanel from "../../components/RailSixSigmaPanel";
import { useAuth } from "../../context/AuthContext";
import {
    Panel, Field, DarkHeader, DateClockChips, statusPill,
    fmtTime, fmtDt, IcnTrain, IcnYard, IcnCrane, IcnBell,
    FlowStep, FlowArrow, IcnCheck, NotificationBell,
    WagonPipelineRibbon, BatchPresetButtons, ShortfallBanner,
} from "./shared";

const H_NAVY = "#0B1A3B";
const ORANGE = "#EA580C";

export default function TransnetYardDashboard() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [notifs, setNotifs] = useState({ items: [], unread_count: 0 });
    const [selected, setSelected] = useState(null);
    const [plannedDate, setPlannedDate] = useState("");
    const [plannedTime, setPlannedTime] = useState("");
    const [releaseNotes, setReleaseNotes] = useState("");
    const [releaseCount, setReleaseCount] = useState("");
    const YARD_CAPACITY = 10;    // TODO: config-driven

    const load = useCallback(async () => {
        try {
            const [cs, nt] = await Promise.all([
                api.get("/rail/consignments?include_completed=true"),
                api.get("/rail/notifications?limit=30"),
            ]);
            setRows(cs.data || []);
            setNotifs(nt.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    const incoming = useMemo(() =>
        rows.filter((c) => c.status === "in_transit_to_transnet"), [rows]);
    const atYard = useMemo(() =>
        rows.filter((c) => ["received_at_transnet", "staged_at_transnet"].includes(c.status)), [rows]);
    const releasing = useMemo(() =>
        rows.filter((c) => c.status === "in_transit_to_grindrod"), [rows]);
    const receivedToday = rows.filter((c) => (c.actual_transnet_arrival_at || "")
        .startsWith(new Date().toISOString().slice(0, 10)));
    const releasedToday = rows.filter((c) => (c.actual_transnet_release_at || "")
        .startsWith(new Date().toISOString().slice(0, 10)));
    const upcoming = rows
        .filter((c) => c.planned_release_at &&
            ["received_at_transnet", "staged_at_transnet"].includes(c.status))
        .slice(0, 8);

    const active = useMemo(() => {
        if (selected?.id) {
            const fresh = rows.find((r) => r.id === selected.id);
            if (fresh) return fresh;
        }
        return atYard[0] || incoming[0];
    }, [rows, selected?.id, atYard, incoming]);
    // Auto-fill planned inputs from selected
    useEffect(() => {
        if (active?.planned_release_at) {
            const d = new Date(active.planned_release_at);
            const iso = d.toISOString();
            setPlannedDate(iso.slice(0, 10));
            setPlannedTime(iso.slice(11, 16));
        }
    }, [active?.id, active?.planned_release_at]);

    const doAction = async (c, action, body = {}, msg = "Updated") => {
        try {
            await api.post(`/rail/consignments/${c.id}/action`, { action, ...body });
            toast.success(msg);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const savePlanned = () => {
        if (!plannedDate || !plannedTime || !active) return;
        const iso = new Date(`${plannedDate}T${plannedTime}`).toISOString();
        doAction(active, "set_planned_release", { planned_release_at: iso },
                 "Planned release saved");
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1600px] mx-auto px-3 md:px-6 py-4 space-y-3">
                <DarkHeader
                    title="Transnet Staging Yard Dashboard"
                    subtitle="Transnet Staging Yard Planner"
                    tint={H_NAVY}
                    icon={<IcnTrain color="#EA580C" size={36} />}
                    right={<DateClockChips notifBtn={
                        <NotificationBell notifs={notifs} onChange={load} testid="tfr-notif-btn" />
                    } />}
                />

                <ShortfallBanner testid="tfr-shortfalls" />
                {active && <WagonPipelineRibbon c={active} />}

                {/* Row 1 — flow of 5 stages + Yard Summary Today */}
                <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-3">
                    <Panel testid="tfr-flow" title={null}>
                        <div className="flex items-stretch gap-1 overflow-x-auto py-2">
                            <FlowStep label="From Mine" sub="In Transit"
                                      done={incoming.length > 0} icon={<IcnTrain size={22} color="#166534" />}
                                      sub2={`${incoming.length}`} />
                            <FlowArrow />
                            <FlowStep label="Arriving Yard" sub="ETA soon" icon={<IcnTrain size={22} color="#166534" />} />
                            <FlowArrow />
                            <FlowStep label="At Staging Yard" sub={`${atYard.length} staged`} active
                                      icon={<IcnYard size={22} />} />
                            <FlowArrow />
                            <FlowStep label="Releasing to Grindrod" sub={`${releasing.length} planned`}
                                      icon={<IcnTrain size={22} color={ORANGE} />} />
                            <FlowArrow />
                            <FlowStep label="To Grindrod" sub={`${releasing.length} in transit`}
                                      icon={<IcnCrane size={22} />} />
                        </div>
                    </Panel>
                    <Panel title="Yard Summary (Today)" testid="tfr-summary">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <Metric label="Trains Received" value={receivedToday.length} icon={<IcnTrain size={18} color="#166534" />} />
                            <Metric label="Trains Released" value={releasedToday.length} icon="🔺" />
                            <Metric label="Avg Staging"     value="—" icon="🕒" />
                            <Metric label="On-Time Releases" value={releasedToday.length ? "100%" : "—"} icon={<IcnCheck size={18} />} />
                        </div>
                    </Panel>
                </div>

                {/* Row 2 — Incoming | At Yard | Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-3">
                    <Panel title="Incoming Trains" testid="tfr-incoming">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                                    <tr className="text-left border-b">
                                        <th className="pb-1 pr-2">Consignment</th>
                                        <th className="pb-1 pr-2">Train</th>
                                        <th className="pb-1 pr-2">ETA</th>
                                        <th className="pb-1 pr-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {incoming.map((c) => (
                                        <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`tfr-in-${c.consignment_no}`}>
                                            <td className="py-1 pr-2 font-mono">{c.consignment_no}</td>
                                            <td className="py-1 pr-2 font-mono">{c.train_id || "—"}</td>
                                            <td className="py-1 pr-2">{fmtTime(c.eta_transnet_arrival_at)}</td>
                                            <td className="py-1 pr-2">
                                                <button onClick={() => doAction(c, "receive_at_transnet", {}, "Received")}
                                                        data-testid={`tfr-receive-${c.consignment_no}`}
                                                        className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-black">RECEIVE</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {incoming.length === 0 && (
                                        <tr><td colSpan={4} className="py-4 text-center text-slate-500">No incoming trains.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title="Trains at Staging Yard" testid="tfr-atyard">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                                    <tr className="text-left border-b">
                                        <th className="pb-1 pr-2">Consignment</th>
                                        <th className="pb-1 pr-2">Train</th>
                                        <th className="pb-1 pr-2 text-right">Wagons</th>
                                        <th className="pb-1 pr-2 text-right">Tons</th>
                                        <th className="pb-1 pr-2">Received</th>
                                        <th className="pb-1 pr-2">Planned Release</th>
                                        <th className="pb-1 pr-2">Status</th>
                                        <th className="pb-1 pr-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {atYard.map((c) => (
                                        <tr key={c.id} onClick={() => setSelected(c)}
                                            className={`border-b hover:bg-slate-50 cursor-pointer
                                                        ${active?.id === c.id ? "bg-amber-50" : ""}`}
                                            data-testid={`tfr-yard-${c.consignment_no}`}>
                                            <td className="py-1 pr-2 font-mono">{c.consignment_no}</td>
                                            <td className="py-1 pr-2 font-mono">{c.train_id || "—"}</td>
                                            <td className="py-1 pr-2 text-right">{c.wagon_count}</td>
                                            <td className="py-1 pr-2 text-right">{Number(c.tonnage || 0).toLocaleString()}</td>
                                            <td className="py-1 pr-2">{fmtTime(c.actual_transnet_arrival_at)}</td>
                                            <td className="py-1 pr-2 text-orange-700 font-bold">
                                                {c.planned_release_at ? fmtDt(c.planned_release_at) : "—"}
                                            </td>
                                            <td className="py-1 pr-2">{statusPill(c.status)}</td>
                                            <td className="py-1 pr-2">
                                                <button onClick={(e) => { e.stopPropagation(); setSelected(c); }}
                                                        data-testid={`tfr-manage-${c.consignment_no}`}
                                                        className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-black">MANAGE</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {atYard.length === 0 && (
                                        <tr><td colSpan={8} className="py-4 text-center text-slate-500">No trains staged.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title="Quick Actions" testid="tfr-quick">
                        <div className="space-y-2 text-[10px] font-black uppercase tracking-widest">
                            <button onClick={() => incoming[0] && doAction(incoming[0], "receive_at_transnet", {}, "Received")}
                                    disabled={!incoming[0]}
                                    className="w-full p-2.5 rounded bg-blue-50 text-blue-800 disabled:opacity-40">
                                🚆 Receive Train
                            </button>
                            <button onClick={() => active && doAction(active, "release_to_grindrod", {}, "Released")}
                                    disabled={!active || !["received_at_transnet","staged_at_transnet"].includes(active?.status)}
                                    className="w-full p-2.5 rounded bg-orange-50 text-orange-800 disabled:opacity-40">
                                ➡ Release to Grindrod
                            </button>
                            <div className="w-full p-2.5 rounded bg-slate-50 text-slate-600 text-center">
                                📅 Yard Capacity / Plan
                            </div>
                        </div>
                    </Panel>
                </div>

                {/* Row 3 — Yard Capacity | Planned Releases | Yard Events */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <Panel title="Yard Capacity" testid="tfr-capacity">
                        <div className="flex items-center gap-3">
                            <div className="w-24 h-24 rounded-full border-8 border-orange-500 grid place-items-center flex-shrink-0">
                                <div className="text-xl font-black">{Math.round(atYard.length * 100 / YARD_CAPACITY)}%</div>
                            </div>
                            <div className="text-xs flex-1">
                                <div className="flex justify-between py-1 border-b"><span>Total Capacity</span><b>{YARD_CAPACITY}</b></div>
                                <div className="flex justify-between py-1 border-b"><span>Tracks Occupied</span><b>{atYard.length}</b></div>
                                <div className="flex justify-between py-1"><span>Available</span><b>{Math.max(0, YARD_CAPACITY - atYard.length)}</b></div>
                            </div>
                        </div>
                    </Panel>
                    <Panel title="Planned Releases (Next 24 Hours)" testid="tfr-planned">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                                    <tr className="text-left border-b">
                                        <th className="pb-1 pr-2">Consignment</th>
                                        <th className="pb-1 pr-2">Train</th>
                                        <th className="pb-1 pr-2">Planned Release</th>
                                        <th className="pb-1 pr-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {upcoming.map((c) => (
                                        <tr key={c.id} className="border-b" data-testid={`tfr-upcoming-${c.consignment_no}`}>
                                            <td className="py-1 pr-2 font-mono">{c.consignment_no}</td>
                                            <td className="py-1 pr-2 font-mono">{c.train_id || "—"}</td>
                                            <td className="py-1 pr-2">{fmtDt(c.planned_release_at)}</td>
                                            <td className="py-1 pr-2">
                                                <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[9px] font-black">
                                                    SCHEDULED
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {upcoming.length === 0 && (
                                        <tr><td colSpan={4} className="py-4 text-center text-slate-500">No planned releases.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                    <Panel title="Yard Events (Today)" testid="tfr-events">
                        <div className="space-y-1.5 text-[11px]">
                            {notifs.items.slice(0, 6).map((n) => (
                                <div key={n.id} className="flex items-start gap-2 border-b last:border-0 pb-1.5">
                                    <span className="text-slate-400 font-mono w-11 flex-shrink-0">{fmtTime(n.created_at)}</span>
                                    <span className="text-slate-800">{n.message}</span>
                                </div>
                            ))}
                            {notifs.items.length === 0 && (
                                <div className="text-slate-500 text-center py-4">No events yet.</div>
                            )}
                        </div>
                    </Panel>
                </div>

                {/* Row 4 — Train details | Set planned release | Release to Grindrod */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <Panel title={active ? `Train Details — ${active.train_id || active.consignment_no}` : "Train Details"} testid="tfr-details">
                        {active ? (
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <Info label="From"           value="Customer Mine" />
                                <Info label="Train ID"       value={active.train_id || "—"} />
                                <Info label="Wagons"         value={active.wagon_count} />
                                <Info label="Tons"           value={Number(active.tonnage || 0).toLocaleString()} />
                                <Info label="Coal Grade"     value={active.coal_grade || active.product} />
                                <Info label="Received"       value={fmtDt(active.actual_transnet_arrival_at)} />
                                <Info label="Planned Release" wide value={fmtDt(active.planned_release_at)} />
                                <Info label="Status" wide    value={statusPill(active.status)} />
                            </div>
                        ) : <div className="text-xs text-slate-500 py-4 text-center">Pick a train.</div>}
                    </Panel>

                    <Panel title="Set / Update Planned Release" testid="tfr-planned-form">
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Planned Date">
                                <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)}
                                       data-testid="tfr-planned-date" className="g-input" />
                            </Field>
                            <Field label="Planned Time">
                                <input type="time" value={plannedTime} onChange={(e) => setPlannedTime(e.target.value)}
                                       data-testid="tfr-planned-time" className="g-input" />
                            </Field>
                        </div>
                        <button onClick={savePlanned} disabled={!active || !plannedDate || !plannedTime}
                                data-testid="tfr-planned-save"
                                className="mt-3 w-full py-2.5 rounded text-white font-black uppercase tracking-widest bg-orange-500 hover:bg-orange-600 disabled:opacity-40">
                            💾 Save Planned Release
                        </button>
                    </Panel>

                    <Panel title={active ? `Release to Grindrod — ${active.train_id || active.consignment_no}` : "Release to Grindrod"}
                           testid="tfr-release">
                        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                            <Info label="Actual Release Date" value={new Date().toLocaleDateString("en-ZA")} />
                            <Info label="Actual Release Time" value={new Date().toLocaleTimeString("en-ZA", {hour:"2-digit",minute:"2-digit"})} />
                            <Info label="Wagons at yard" value={Math.max(0, (active?.wagons_at_transnet || 0) - (active?.wagons_dispatched_to_grindrod || 0))} />
                            <Info label="Next Destination" value="Grindrod (Tippler)" />
                        </div>
                        <Field label="Wagons to release now">
                            <input type="number" min={1}
                                   max={Math.max(0, (active?.wagons_at_transnet || 0) - (active?.wagons_dispatched_to_grindrod || 0))}
                                   value={releaseCount}
                                   onChange={(e) => setReleaseCount(e.target.value)}
                                   placeholder="Leave blank to release all remaining"
                                   className="g-input" data-testid="tfr-release-count" />
                            <BatchPresetButtons
                                max={Math.max(0, (active?.wagons_at_transnet || 0) - (active?.wagons_dispatched_to_grindrod || 0))}
                                onPick={(n) => setReleaseCount(String(n))}
                                testidPrefix="tfr-release" />
                        </Field>
                        <Field label="Comments (optional)">
                            <input value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)}
                                   className="g-input" placeholder="Enter comments…" />
                        </Field>
                        <button onClick={() => active && doAction(active, "release_to_grindrod",
                                                                    {
                                                                        count: releaseCount ? Number(releaseCount) : undefined,
                                                                        notes: releaseNotes || undefined,
                                                                    },
                                                                    releaseCount ? `${releaseCount} wagons released` : "Released to Grindrod")
                                                     .then(() => setReleaseCount(""))}
                                disabled={!active || !["received_at_transnet","staged_at_transnet","in_transit_to_grindrod","received_at_grindrod","waiting_for_tipping","tipping_in_progress"].includes(active?.status)
                                          || Math.max(0, (active?.wagons_at_transnet || 0) - (active?.wagons_dispatched_to_grindrod || 0)) === 0}
                                data-testid="tfr-release-btn"
                                className="mt-3 w-full py-2.5 rounded text-white font-black uppercase tracking-widest bg-orange-600 hover:bg-orange-700 disabled:opacity-40">
                            🚆 Release Wagons to Grindrod
                        </button>
                    </Panel>
                </div>

                {/* Process flow strip for the selected train */}
                {active && (
                    <Panel title={`Process Flow — ${active.train_id || active.consignment_no}`} testid="tfr-processflow">
                        <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
                            <FlowStep label="Dispatched from Mine" sub={fmtTime(active.actual_dispatch_at)}
                                      done={!!active.actual_dispatch_at} icon="🚂" />
                            <FlowArrow />
                            <FlowStep label="In Transit to Yard" sub={active.eta_transnet_arrival_at ? "ETA " + fmtTime(active.eta_transnet_arrival_at) : "—"}
                                      done={active.status !== "in_transit_to_transnet" && !!active.actual_transnet_arrival_at} icon={<IcnTrain size={22} color="#166534" />} />
                            <FlowArrow />
                            <FlowStep label="Received at Yard" sub={fmtTime(active.actual_transnet_arrival_at)}
                                      done={!!active.actual_transnet_arrival_at} icon={<IcnYard size={22} />} />
                            <FlowArrow />
                            <FlowStep label="Staged at Yard" sub={active.planned_release_at ? "Release " + fmtTime(active.planned_release_at) : "—"}
                                      active={active.status === "staged_at_transnet" || active.status === "received_at_transnet"}
                                      icon={<IcnYard size={22} color={ORANGE} />} />
                            <FlowArrow />
                            <FlowStep label="Released to Grindrod" sub={fmtTime(active.actual_transnet_release_at)}
                                      done={!!active.actual_transnet_release_at} icon={<IcnTrain size={22} color={ORANGE} />} />
                            <FlowArrow />
                            <FlowStep label="In Transit → Grindrod" sub={fmtTime(active.eta_grindrod_arrival_at)}
                                      done={active.status !== "in_transit_to_grindrod" && !!active.actual_grindrod_arrival_at}
                                      active={active.status === "in_transit_to_grindrod"} icon={<IcnTrain size={22} />} />
                            <FlowArrow />
                            <FlowStep label="Received Grindrod" sub={fmtTime(active.actual_grindrod_arrival_at)}
                                      done={!!active.actual_grindrod_arrival_at} icon={<IcnCrane size={22} />} />
                        </div>
                    </Panel>
                )}

                {/* Live Six-Sigma Analysis + production recommendations */}
                <RailSixSigmaPanel role="transnet" />

                <div className="text-[10px] text-slate-400 pb-6">
                    All times local (Africa/Johannesburg). Signed in as <b>{user?.name}</b>.
                </div>
            </main>
        </div>
    );
}

function Metric({ label, value, icon }) {
    return (
        <div className="rounded bg-slate-50 border p-2">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                {typeof icon === "string" ? <span>{icon}</span> : icon}
                <span>{label}</span>
            </div>
            <div className="text-xl font-black">{value}</div>
        </div>
    );
}
function Info({ label, value, wide }) {
    return (
        <div className={wide ? "col-span-2" : ""}>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className="font-bold">{value}</div>
        </div>
    );
}
